import { FC, ReactNode, useEffect, useState } from 'react';

import classNames from 'classnames';

import CustomSelect, { CustomSelectOption } from 'bloko/blocks/customSelect';
import HSpacing from 'bloko/blocks/hSpacing';
import Icon, { IconColor, IconLink, IconScale, IconView } from 'bloko/blocks/icon';

import styles from 'components/Admin/Reports/ActsReport/EditingActsTable/EditingAct/EditingSelectorField/EditingSelectorField.module.less';

interface DictionaryItem {
    value: string;
    label: string;
}

interface EditingSelectorFieldProps {
    value: string | undefined;
    dictionary: DictionaryItem[];
    newValue: string | undefined;
    setNewValue: (value: string) => void;
    resetNewValue: () => void;
    children: ReactNode;
    size?: 'small' | 'medium';
    placeholder?: string;
    disabled?: boolean;
}

const EditingSelectorField: FC<EditingSelectorFieldProps> = ({
    value,
    dictionary,
    newValue,
    setNewValue,
    resetNewValue,
    children,
    size,
    placeholder,
    disabled,
}) => {
    const [isEditing, setEditing] = useState(false);

    const onReset = () => {
        setEditing(false);
        resetNewValue();
    };

    useEffect(() => {
        if (value && newValue === undefined) {
            setEditing(false);
        }
    }, [value, newValue]);

    if (isEditing) {
        return (
            <div
                className={classNames(styles.actsEditingField, {
                    [styles.actsEditingFieldSmall]: size === 'small',
                    [styles.actsEditingFieldMedium]: size === 'medium',
                })}
            >
                <div className={styles.actsEditingSelect}>
                    <CustomSelect
                        value={newValue === undefined ? value || '' : newValue}
                        onChange={setNewValue}
                        emptyPlaceholder={placeholder}
                        disabled={disabled}
                    >
                        {dictionary.map(({ value, label }) => (
                            <CustomSelectOption key={value} value={value}>
                                {label}
                            </CustomSelectOption>
                        ))}
                    </CustomSelect>
                </div>
                <HSpacing base={2} />
                <IconLink onClick={onReset} data-qa="select-reset">
                    <Icon
                        view={IconView.Cross}
                        initial={IconColor.Gray80}
                        scale={IconScale.Small}
                        data-qa="select-reset-icon"
                    />
                </IconLink>
            </div>
        );
    }

    return (
        <div
            className={classNames('acts-editing-field', {
                'acts-editing-field-small': size === 'small',
                'acts-editing-field-medium': size === 'medium',
            })}
        >
            {children}
            <HSpacing base={2} />
            <IconLink onClick={() => setEditing(true)}>
                <Icon view={IconView.PenSquare} initial={IconColor.Gray50} scale={IconScale.Small} />
            </IconLink>
        </div>
    );
};

export default EditingSelectorField;
